"use client";

import React, { useId } from "react";
import { Box } from "@mui/material";
import OptionCard, { StepHeading } from "../canvas/OptionCard";
import type { VariantAxis } from "../canvas/variant-resolver";
import { formatMoney } from "../canvas/variant-price";
import { localized } from "./artwork-types";
import type { SetMaterialOption } from "./art-sets";

type T = ((key: string, values?: Record<string, string | number>) => string) & {
  has?: (key: string) => boolean;
};

/**
 * "Foto papir" / "Platno" in the shop's words — the editor's label, falling
 * back to the definition's own when a locale has none.
 */
export function materialLabel(
  material: string,
  tEditor: T,
  axes: VariantAxis[],
  locale: string,
): string {
  const key = `canvasMaterial_${material}`;
  const text = tEditor(key);
  if (text && text !== key) return text;
  const def = axes.find((a) => a.key === "material")?.values.find((v) => v.value === material);
  return def ? localized(def.label, locale) : material;
}

/**
 * What a material comes as, read off the variants: "Mat ali sijaj, zvit v
 * tulcu" for paper, "Napeto na lesen okvir ali zvito v tulcu" for canvas.
 * A surface is named only when there is a choice of it — every canvas is
 * matte, and saying so on the card adds nothing.
 */
export function materialSummary(option: SetMaterialOption, tArt: T): string {
  const word = (value: string) => {
    const key = `sets.opt.${option.material}.${value}`;
    return tArt.has?.(key) ? tArt(key) : value;
  };
  const or = ` ${tArt("sets.orWord")} `;
  const parts: string[] = [];
  if (option.surfaces.length > 1) {
    const surfaces = [...option.surfaces].sort((a, b) =>
      a === "matte" ? -1 : b === "matte" ? 1 : 0,
    );
    parts.push(surfaces.map((s) => word(s)).join(or));
  }
  // Stretched first: the ready-to-hang option is the one worth naming first.
  const mounts = [...option.mounts].sort((a, b) =>
    a === "stretched" ? -1 : b === "stretched" ? 1 : 0,
  );
  if (mounts.length) parts.push(mounts.map((m) => word(m)).join(or));
  const text = parts.join(", ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

export interface SetMaterialPickerProps {
  options: SetMaterialOption[];
  material: string | undefined;
  onChange: (material: string) => void;
  axes: VariantAxis[];
  locale: string;
  tArt: T;
  tEditor: T;
}

/**
 * The first question on a set page: photo paper or canvas.
 *
 * Unlike the single-work page, each card carries a price: what the whole set
 * costs from in that material. Choosing between two materials for three
 * pieces is a real money decision, and the totals differ enough to matter.
 */
export default function SetMaterialPicker({
  options,
  material,
  onChange,
  axes,
  locale,
  tArt,
  tEditor,
}: SetMaterialPickerProps) {
  const headingId = useId();
  if (options.length < 2) return null;
  const heading = (() => {
    const text = tEditor("canvasMaterial");
    return text && text !== "canvasMaterial" ? text : "Material";
  })();
  return (
    <Box>
      <StepHeading id={headingId}>{heading}</StepHeading>
      <Box
        role="radiogroup"
        aria-labelledby={headingId}
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
      >
        {options.map((option) => (
          <OptionCard
            key={option.material}
            selected={material === option.material}
            onClick={() => onChange(option.material)}
            title={materialLabel(option.material, tEditor, axes, locale)}
            subtitle={materialSummary(option, tArt)}
            price={tArt("fromPrice", {
              price: formatMoney(option.from.amount, option.from.currency, locale),
            })}
          />
        ))}
      </Box>
    </Box>
  );
}
